import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Input, { LEFT, CENTER, RIGHT } from '../Input/InputView';
import { UP, DOWN, ENTER } from './InputAutocompleteConstants';
import { isNumeric } from '../../../js/app/react/validators';

export const parseValue = (value, isInteger) => {
  if (isInteger) {
    if (value === '' || !isNumeric(value)) {
      return '';
    }
    return parseInt(value, 10).toString();
  }

  return value;
};

export const optionComparator = (trimmedLowercaseSearchString, getOptionValue) => (
  { option: l },
  { option: r },
) => {
  const leftValue = (getOptionValue && l ? getOptionValue(l) : l).trim().toLowerCase();
  const rightValue = (getOptionValue && r ? getOptionValue(r) : r).trim().toLowerCase();
  const left = leftValue.startsWith(trimmedLowercaseSearchString) ? 0 : 1;
  const right = rightValue.startsWith(trimmedLowercaseSearchString) ? 0 : 1;
  if (left !== right) {
    return left - right;
  }
  if (leftValue < rightValue) {
    return -1;
  } else if (leftValue === rightValue) {
    return 0;
  }
  return 1;
};

export default class InputAutocomplete extends Component {
  constructor(props) {
    super(props);
    this.onOptionRef = this.onOptionRef.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.state = {
      selectedIndex: 0,
      isOpen: false,
      options: [],
      indexes: [],
    };
  }

  UNSAFE_componentWillMount() {
    const { value, options, fetchOptions, maxOptions, getOptionValue } = this.props;

    if ((value || '').length > 0) {
      const searchArray = (value || '').trim().toLowerCase();
      const mappedOptions = options.map((option, index) => ({
        option,
        index,
      }));
      const filteredOptions = fetchOptions
        ? mappedOptions
        : mappedOptions
            .filter(({ option }) =>
              (getOptionValue && option ? getOptionValue(option) : option)
                .toLowerCase()
                .startsWith(searchArray),
            )
            .slice(0, maxOptions);
      filteredOptions.sort(optionComparator(searchArray, getOptionValue));
      this.setState({
        options: filteredOptions.map(({ option }) => option),
        indexes: filteredOptions.map(({ index }) => index),
        selectedIndex: 0,
      });
      return;
    }
    const filteredOptions = fetchOptions ? options : options.slice(0, maxOptions);
    this.setState({
      options: filteredOptions,
      indexes: filteredOptions.map((_, index) => index),
      selectedIndex: 0,
    });
  }

  UNSAFE_componentWillReceiveProps({ options, value, maxOptions, getOptionValue }) {
    const { fetchOptions } = this.props;

    if ((value || '').length > 0) {
      const searchArray = (value || '').trim().toLowerCase();
      const mappedOptions = options.map((option, index) => ({
        option,
        index,
      }));
      const filteredOptions = fetchOptions
        ? mappedOptions
        : mappedOptions
            .filter(({ option }) =>
              (getOptionValue && option ? getOptionValue(option) : option)
                .toLowerCase()
                .startsWith(searchArray),
            )
            .slice(0, maxOptions);
      filteredOptions.sort(optionComparator(searchArray, getOptionValue));
      this.setState({
        options: filteredOptions.map(({ option }) => option),
        indexes: filteredOptions.map(({ index }) => index),
        selectedIndex: 0,
      });
      return;
    }

    const filteredOptions = fetchOptions ? options : options.slice(0, maxOptions);

    this.setState({
      options: filteredOptions,
      indexes: filteredOptions.map((_, index) => index),
      selectedIndex: 0,
    });
  }

  onOptionRef(index) {
    return domElement => {
      if (!this.optionRefs) {
        this.optionRefs = {};
      }

      this.optionRefs[index] = domElement;
    };
  }

  onChange(event) {
    const { fetchOptions, onChange, isInteger } = this.props;

    const {
      target: { value },
    } = event;

    const valueParsed = parseValue(value, isInteger);

    const updatedEventObj = {
      target: {
        value: valueParsed,
      },
    };

    const isOpen = (event.target.value || '').length > 0;

    this.setState({ isOpen });

    onChange(updatedEventObj);

    if (isOpen && fetchOptions) {
      fetchOptions(value);
    }
  }

  onKeyDown(event) {
    const { value, onChange, getOptionValue } = this.props;
    let { selectedIndex } = this.state;
    const { options } = this.state;
    const key = event.keyCode;
    if (key === UP) {
      if (selectedIndex === 0) {
        selectedIndex = this.state.options.length;
      }
      selectedIndex -= 1;
    }

    if (key === DOWN) {
      selectedIndex = (selectedIndex + 1) % this.state.options.length;
    }

    if (selectedIndex !== this.state.selectedIndex) {
      this.setState({
        selectedIndex,
      });
      if (this.optionRefs && this.optionRefs[selectedIndex]) {
        this.optionRefs[selectedIndex].scrollIntoViewIfNeeded();
      }
    }

    if (key === ENTER) {
      if (options.length !== 0 && value.length > 0) {
        onChange({
          target: {
            triggeredBy: 'option',
            value: getOptionValue
              ? getOptionValue(this.state.options[selectedIndex])
              : this.state.options[selectedIndex],
          },
        });
      }
      this.setState({
        isOpen: false,
      });
      event.preventDefault();
      return false;
    }
    return false;
  }

  render() {
    const {
      value,
      onChange,
      placeholder,
      className,
      showEmptyCustomMessage = false,
      customEmptyMessage,
      renderOption,
      getOptionValue,
      getOptionKey,
      placeholderClassName,
      focusedClassName,
      isUnder,
      inputWrapperClass,
      alignment,
      htmlPlaceholder,
      inputmode,
      id,
    } = this.props;

    const { options, isOpen } = this.state;

    return (
      <div className={`input-autocomplete ${isOpen ? 'open' : ''} ${inputWrapperClass || ''}`}>
        <Input
          className={className}
          placeholder={placeholder}
          placeholderClassName={placeholderClassName}
          focusedClassName={focusedClassName}
          value={value}
          type="text"
          inputmode={inputmode}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
          isUnder={isUnder}
          alignment={alignment}
          htmlPlaceholder={htmlPlaceholder}
          id={id}
        />
        {options.length > 0 && isOpen && (
          <div className="results adv-scrollbars">
            <ul className="results-list no-horizontal-scroll">
              {options.map((option, index) => (
                <li
                  key={getOptionKey ? getOptionKey(option) : option}
                  className="results-item"
                  ref={this.onOptionRef(index)}
                >
                  <button
                    type="button"
                    className={`result-item-inner ${
                      index === this.state.selectedIndex ? 'selected' : ''
                    }`}
                    onClick={() => {
                      onChange({
                        target: {
                          triggeredBy: 'option',
                          value: getOptionValue && option ? getOptionValue(option) : option,
                        },
                      });

                      this.setState({ isOpen: false });
                    }}
                  >
                    {renderOption ? renderOption(option, this.state.indexes[index]) : option}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {options.length === 0 && value.length > 0 && isOpen && showEmptyCustomMessage && (
          <div className="no-result">
            <button
              className="no-result-content"
              type="button"
              onClick={() => this.setState({ isOpen: false })}
            >
              {customEmptyMessage}
            </button>
          </div>
        )}
      </div>
    );
  }
}

InputAutocomplete.defaultProps = {
  className: '',
  value: '',
  options: [],
  showEmptyCustomMessage: false,
  customEmptyMessage: '',
  renderOption: null,
  fetchOptions: null,
  getOptionValue: null,
  getOptionKey: null,
  placeholderClassName: '',
  isUnder: undefined,
  inputWrapperClass: null,
  alignment: CENTER,
  htmlPlaceholder: null,
  isInteger: false,
  maxOptions: 4,
  focusedClassName: undefined,
  inputmode: undefined,
  id: undefined,
};

InputAutocomplete.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  placeholderClassName: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  focusedClassName: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  showEmptyCustomMessage: PropTypes.bool,
  customEmptyMessage: PropTypes.string,
  renderOption: PropTypes.func,
  fetchOptions: PropTypes.func,
  getOptionValue: PropTypes.func,
  getOptionKey: PropTypes.func,
  isUnder: PropTypes.bool,
  inputWrapperClass: PropTypes.string,
  alignment: PropTypes.oneOf([LEFT, CENTER, RIGHT]),
  htmlPlaceholder: PropTypes.string,
  isInteger: PropTypes.bool,
  maxOptions: PropTypes.number,
  inputmode: PropTypes.string,
  id: PropTypes.string,
};
